import type { Maybe } from '@/types/globals';
import { Inject, Injectable } from '@angular/core';
import { BiLoggerService } from '@core/services/logger/bi-logger.service';
import { BiBannerType } from '@core/services/logger/interfaces/bi.enums';
import { AwarenessService } from '@modules/awareness/services/awareness.service';
import { CaseTypeEnum } from '@modules/order/models/case-type.enum';
import { OrderQuery } from '@modules/order/state/order-query';
import { PatientAppIframeCommunicationService } from '@modules/patient/services/patient-app-iframe-communication.service';
import { PatientQuery } from '@modules/patient/state/patient-query';
import { PatientAppLoadStateService } from '@modules/rx-for-doctor/services/patient-app-load-state.service';
import { Platforms, SoftwareOptions } from '@shared/models/enums/enums';
import { ProcedureMap } from '@shared/models/procedure-map';
import { RoleTypeEnum } from '@shared/models/role-type';
import { RxConfiguration } from '@shared/models/rx-configuration';
import { RxAppSuiteBanner } from '@shared/models/rx-models/interfaces/rx-model';
import { HostPlatformService } from '@shared/services/host-platform.service';
import { ProcedureSectionVisibilityService } from '@shared/services/procedure-section-visibility.service';
import { ScanOptionsVisibilityService } from '@shared/services/scan-options-visibility.service';
import { registerForDesignSuit } from '@core/services/logger/bi-actions';
import {
	APPLIANCE_DETAILS_SECTION_VISIBLE$,
	DENTURE_DETAILS_SECTION_VISIBLE$,
	IS_NOTES_VISIBLE$
} from '@shared/services/sections-visibility.tokens';
import { SoftwareOptionsService } from '@shared/services/software-options.service';
import { AuthInfoQuery } from '@shared/store/authInfo/auth-info-query';
import { ShellQuery } from '@shared/store/shell/shell-query';
import { ShellStore } from '@shared/store/shell/shell-store';
import { enumEquals } from '@shared/utils/enum-utils';
import debounce from 'lodash.debounce';
import { BiSessionInfo } from '@itero/rx-attachments/lib/models/analytics/BiSessionInfo';
import { BehaviorSubject, combineLatest, merge, Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { RxForDoctorQuery } from './state/rx-for-doctor-query';
import { IntuitiveFlowService } from '@shared/services/intuitive-flow.service';

@Injectable()
export class RxForDoctorFacade {
	rxConfiguration$: Observable<Maybe<RxConfiguration>> = this.shellQuery.rxConfiguration$;
	isChairSideMillingAvailableForDownload$ = new BehaviorSubject<boolean>(false);
	companyCountryCode$: Observable<Maybe<string>> = this.shellQuery.companyCountryCode$;
	companyId$: Observable<Maybe<number>> = this.shellQuery.companyId$;
	isReadOnly$: Observable<boolean> = this.shellQuery.isReadOnly$;
	contactId$: Observable<Maybe<number>> = this.shellQuery.contactId$;
	isReferralWorkflowPractice$: Observable<boolean> = this.rxForDoctorQuery.isReferralWorkflowPractice$;
	isRxValidForSave$: Observable<Maybe<boolean>> = this.shellQuery.isRxValidForSave$;
	isRxSent$: Observable<boolean> = this.shellQuery.isRxSent$;
	linkToTheLearnNowForDesignSuite$: Observable<Maybe<string>> = this.shellQuery.linkToTheLearnNowForDesignSuite$;
	isTeethDiagramAreaVisible$: Observable<boolean> = this.sectionVisibilityService.isTeethDiagramAreaVisible$;

	isMultiBiteVisible$: Observable<Maybe<boolean>> = this.orderQuery.procedureMap$.pipe(map(x => x?.MultiBiteIsEnabled));
	procedureMap$: Observable<Maybe<ProcedureMap>> = this.orderQuery.procedureMap$;

	// TODO: copy to intuitive-scan-options component, manage all flag visibilities inside intuitive-scan-options comp
	isScanOptionsVisible$: Observable<boolean> = this.sectionVisibilityService.isScanOptionsVisible$;

	viewMode$ = this.shellQuery.viewMode$;
	isProcedureFlow$: Observable<boolean> = this.shellQuery.isProcedureFlow$;

	isPatientAppReady$: Observable<boolean> = this.patientAppLoadStateService.isPatientAppReady$;

	patientGuid$: Observable<Maybe<string>> = this.patientQuery.patientGuid$;
	patientFullName$: Observable<string | undefined> = this.patientQuery.patient$.pipe(map(p => p?.FullName));

	isPatientReadyAndLoaded$: Observable<boolean> = this.patientAppLoadStateService.isPatientReadyAndLoaded$;

	attachmentsRxId$ = this.shellQuery.attachmentsRxId$;
	featureToggles$ = this.shellQuery.featureToggles$;
	companySoftwareOptions$ = this.shellQuery.companySoftwareOptions$;
	authInfo$ = this.authInfoQuery.authInfo$;
	userRole$: Observable<RoleTypeEnum> = this.shellQuery.userRole$;
	appSettings$ = this.shellQuery.appSettings$;

	biSessionInfo$: Observable<BiSessionInfo> = this.biLoggerService.biSessionInfo$;

	// eslint-disable-next-line @typescript-eslint/no-unsafe-call
	debouncedHandleResize = debounce<() => void>(this.normalizeScroll.bind(this), 500);

	readonly facadeName = 'RxForDoctorFacade';

	get noticeComponentConfig(): { companyCountryCode: string; noticeTranslationPath: string }[] {
		return [{ companyCountryCode: 'RU', noticeTranslationPath: 'NoticeForRussianCompany' }];
	}

	isReadOnlyBannerVisible$ = combineLatest([
		this.isReadOnly$,
		this.isRxSent$,
		this.hostPlatformService.isScanner$,
		this.orderQuery.isDraftSelected$
	]).pipe(
		map(([isReadOnly, isRxSent, isScanner, isDraftSelected]) => (isScanner ? isReadOnly : isReadOnly && isRxSent && !isDraftSelected))
	);

	isShowEarlyAccess$: Observable<Maybe<boolean>> = this.awarenessService.showEarlyAccessSWO$.pipe(
		map(
			(bannerSettings: RxAppSuiteBanner) =>
				bannerSettings.enableBannerMiniAppsInRX || bannerSettings.enableInformAccessBannerInRXAndMiniApp
		)
	);

	isFallbackBannerAvailable$ = this.intuitiveFlowService.isFallbackBannerAvailable$;

	constructor(
		private readonly patientQuery: PatientQuery,
		private readonly orderQuery: OrderQuery,
		private readonly shellQuery: ShellQuery,
		private readonly shellStore: ShellStore,
		private readonly rxForDoctorQuery: RxForDoctorQuery,
		private readonly softwareOptionsService: SoftwareOptionsService,
		private readonly patientAppLoadStateService: PatientAppLoadStateService,
		private readonly patientAppIframeCommunicationService: PatientAppIframeCommunicationService,
		private readonly hostPlatformService: HostPlatformService,
		private readonly authInfoQuery: AuthInfoQuery,
		private readonly awarenessService: AwarenessService,
		private readonly biLoggerService: BiLoggerService,
		// @TODO: extract dedicated visibility tokens for the remaining flags still consumed here:
		//   - isTeethDiagramAreaVisible$
		//   - isScanOptionsVisible$
		// then this dependency can be dropped in favour of per-flag InjectionTokens
		// (mirrors the pattern already used for APPLIANCE_DETAILS_SECTION_VISIBLE$ / DENTURE_DETAILS_SECTION_VISIBLE$ / IS_NOTES_VISIBLE$).
		private readonly sectionVisibilityService: ProcedureSectionVisibilityService,
		private readonly scanOptionsVisibilityService: ScanOptionsVisibilityService,
		@Inject(APPLIANCE_DETAILS_SECTION_VISIBLE$) public readonly isApplianceDetailsVisible$: Observable<boolean>,
		@Inject(DENTURE_DETAILS_SECTION_VISIBLE$) public readonly isDentureDetailsVisible$: Observable<boolean>,
		@Inject(IS_NOTES_VISIBLE$) public readonly isNotesVisible$: Observable<boolean>,
		private readonly intuitiveFlowService: IntuitiveFlowService
	) {}

	initStoreValues(): Observable<any> {
		return merge(
			// TODO: copy to intuitive-scan-options component, do not copy to intuitive-rx-prcoedure-selected
			this.setIsNiriCaptureVisibility(),
			this.setSleeveConfirmationVisibility(),
			this.setPreTreatmentVisibilityForV0(),
			// TODO: replace with observable, remove behavior subject
			this.setIsChairSideMillingAvailableForDownload(),
			this.setIsPalatalFeedbackVisible()
		);
	}

	setPatientAppReadyState(isReady: boolean) {
		this.patientAppIframeCommunicationService.setPatientAppReadyState(isReady);
	}

	isDownloadPdfButtonVisible(): Observable<boolean> {
		return combineLatest([this.isReferralWorkflowPractice$, this.shellQuery.isRxSent$]).pipe(
			map(([isReferralWorkflowPractice, isRxSent]) => {
				return !!(isReferralWorkflowPractice && !isRxSent);
			})
		);
	}

	updateRegisterForDesignSuite(companyId: number, selectedValue: string, bannerType: string) {
		if (enumEquals(bannerType, BiBannerType.BannerRegister)) {
			this.awarenessService.updateRegisterForDesignSuite(companyId);
		}
		this.biLoggerService.userActionEvent(registerForDesignSuit(selectedValue, bannerType));
	}

	get hostPlatform(): Platforms {
		return this.hostPlatformService.hostPlatform;
	}

	rxComponentsNormalizeScrollPosition() {
		window.addEventListener('resize', this.debouncedHandleResize);
	}

	private normalizeScroll(): void {
		document.activeElement?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
	}

	private setIsNiriCaptureVisibility() {
		return this.scanOptionsVisibilityService.isNiriCaptureVisible$.pipe(
			tap(isNiriCaptureVisible => this.shellStore.update({ isNiriCaptureVisible }))
		);
	}

	private setSleeveConfirmationVisibility() {
		return this.scanOptionsVisibilityService.isSleeveConfirmationVisible$.pipe(
			tap(isSleeveConfirmationVisible => this.shellStore.update({ isSleeveConfirmationVisible }))
		);
	}

	private setPreTreatmentVisibilityForV0() {
		return combineLatest([
			this.hostPlatformService.isScanner$,
			this.orderQuery.caseType$,
			this.shellQuery.isRxSent$,
			this.shellQuery.prePrepScan$
		]).pipe(
			map(([isHostPlatformScanner, caseType, isRxSent, isPreTreatment]) => {
				if (isHostPlatformScanner) {
					return (
						(isRxSent && isPreTreatment) ||
						[CaseTypeEnum.Restorative, CaseTypeEnum.ChairSideMilling, CaseTypeEnum.ChairSideMillingGlidewellIo].includes(
							caseType?.Id
						)
					);
				}

				return false;
			}),
			tap(isPreTreatmentVisibleForV0 => this.shellStore.update({ isPreTreatmentVisibleForV0 }))
		);
	}

	private setIsChairSideMillingAvailableForDownload() {
		return combineLatest([
			this.softwareOptionsService.hasScannerSoftwareOptions$([
				SoftwareOptions.ChairSideMillingE4D,
				SoftwareOptions.ChairSideMillingOther,
				SoftwareOptions.ChairSideMillingGlidewell
			]),
			this.orderQuery.isCurrentCaseType$(CaseTypeEnum.ChairSideMilling)
		]).pipe(
			map(([hasSuitableSoftewareOptions, isChairSideMillingCaseType]) => isChairSideMillingCaseType && hasSuitableSoftewareOptions),
			tap(result => this.isChairSideMillingAvailableForDownload$.next(result))
		);
	}

	private setIsPalatalFeedbackVisible() {
		return this.scanOptionsVisibilityService.isPalatalFeedbackVisible$.pipe(
			tap(isPalatalFeedbackVisible => this.shellStore.update({ isPalatalFeedbackVisible }))
		);
	}
}
